import { useState, useRef, useEffect, useCallback } from 'react';

const SCROLL_THRESHOLD = 4;
const DESKTOP_BP = 1024;

const isDesktop = () => window.innerWidth >= DESKTOP_BP;

// Desktop: el scroll vive en .wc-country-detail (= detailEl). Antes vivía en
// __view, lo que causaba doble-scroll con el shell (.content_div).
// Mobile/Tablet: el scroll sigue siendo del shell .content_div.
const getScrollContainer = (detailEl /* , viewEl */) =>
  isDesktop() ? detailEl : detailEl?.closest('.content_div');

export const useCountryDetailScroll = activeView => {
  const [viewScrolled, setViewScrolled] = useState(false);
  const [viewAtBottom, setViewAtBottom] = useState(true);
  const detailRef = useRef(null);
  const viewRef = useRef(null);

  const resetScroll = useCallback(() => {
    setViewScrolled(false);
    setViewAtBottom(true);
    if (viewRef.current) viewRef.current.scrollTop = 0;
    const tabpanel = detailRef.current?.closest(
      '[data-role="mundial2026-tabpanel"]',
    );
    if (tabpanel) tabpanel.scrollTop = 0;
    const contentDiv = detailRef.current?.closest('.content_div');
    if (contentDiv) contentDiv.scrollTop = 0;
  }, []);

  useEffect(() => {
    let container = getScrollContainer(detailRef.current, viewRef.current);

    const handleScroll = () => {
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;

      setViewScrolled(scrollTop > SCROLL_THRESHOLD);

      if (isDesktop()) {
        setViewAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
      }
    };

    const handleResize = () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      container = getScrollContainer(detailRef.current, viewRef.current);
      if (container)
        container.addEventListener('scroll', handleScroll, { passive: true });
    };

    let resizeObserver;
    if (container && isDesktop()) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const { scrollTop, scrollHeight, clientHeight } = container;
          setViewAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
        });
      });
      resizeObserver.observe(container);
    }

    if (container) {
      requestAnimationFrame(handleScroll);
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('resize', handleResize);

    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [activeView]);

  return {
    detailRef,
    viewRef,
    viewScrolled,
    viewAtBottom,
    resetScroll,
  };
};
